/**
 * Bastion Wall artwork registry, tiered by building level.
 *
 * Each tier's source render frames a ring viewed from an elevated angle,
 * plus tier-specific extras that are NOT part of the ring itself — tier 3
 * adds watchtowers rising above the ring line, tier 5 adds anti-vehicle
 * barrier props scattered outside it, tier 8 (the final tier) adds gun
 * turrets plus an energy-shield mesh strung between them. Those extras vary
 * each render's overall alpha bounding box independently of the ring's
 * actual size, so tiers are anchored on the ring band (ringTop/ringBottom,
 * as a fraction of the ORIGINAL uncut image's natural height — see below)
 * rather than the image's own full bounding box — otherwise the wall would
 * visibly shrink or jump every time a taller/wider tier replaces a shorter
 * one. `colony-city-renderer.ts`'s resolveWallRingDraw scales every tier so
 * its ring band lands at the same on-screen size and position; only the
 * extras (spilling above/below that band) grow tier over tier.
 *
 * Band fractions below were measured directly off each original (pre-split)
 * shipped file (alpha channel thresholded at 50% to ignore soft glow/vignette
 * halos, then scanned in horizontal bands to find where the wide ring
 * silhouette starts/ends, as opposed to the narrow tower-spike/barrier-prop
 * regions above and below it) — re-measure if any of these four files are
 * ever re-exported at different framing.
 *
 * Each tier's art ships as TWO files, not one: `rearSrc` (the far/top half
 * of the ring, no gate — drawn behind every building) and `frontSrc` (the
 * near/bottom half, including the gate — drawn in front of every building).
 * This is what makes buildings read as genuinely standing INSIDE the ring
 * rather than under a single flat backdrop layer: the camera's elevated
 * angle means the near wall segment is physically closer to the viewer than
 * the buildings it encircles, and should occlude them the way the far
 * segment never does. Both halves were cropped from the same original image
 * at its exact vertical midpoint (naturalHeight / 2) — see
 * colony-city-renderer.ts's splitWallRingDraw, which re-derives the correct
 * on-screen split point from each half's own loaded natural height rather
 * than assuming an exact 50/50 (in case a future tier isn't cropped
 * perfectly evenly).
 */

export interface WallArtTier {
  /** Bastion Wall level (inclusive) at which this tier's art becomes current. */
  minLevel: number;
  /** Far/top half — no gate, drawn behind every building. */
  rearSrc: string;
  /** Near/bottom half — includes the gate, drawn in front of every building. */
  frontSrc: string;
  /** Top of the ring band, as a fraction of the ORIGINAL (pre-split) image's natural height. */
  ringTop: number;
  /** Bottom of the ring band, as a fraction of the ORIGINAL (pre-split) image's natural height. */
  ringBottom: number;
  /**
   * Per-tier art-direction correction, multiplied into BOTH the ring's drawn
   * width and its band height so the ring shrinks without distorting.
   * Omit for 1 (the default), which is the normal case.
   *
   * Distinct from ringTop/ringBottom: those say where the ring IS inside its
   * source image (a measurement), while this says how big it should be drawn
   * relative to the grid (a judgement). Shrinking a tier by inflating its
   * measured band would scale only the height — width is pinned to the grid
   * independently — and squash the wall.
   *
   * Mirrors BuildingArtTier.scaleMultiplier, which solves the same problem
   * for per-plot building art.
   */
  scaleMultiplier?: number;
}

/**
 * Ordered ascending by minLevel. Adding a new tier is a one-line addition
 * here — nothing else needs to change. Tier 8 is the final tier shipped so
 * far (moved up from tier 7 — see the L5 tier's comment below): wallArtForLevel's
 * "highest tier whose minLevel is met" rule already makes it the ceiling for
 * every level above it too (9, 10, ...), so nothing special was needed to
 * make it "the last" one, or to move where "the last" one starts.
 */
export const WALL_ART_TIERS: WallArtTier[] = [
  { minLevel: 1, rearSrc: '/walls/bastion-wall-t1-rear.webp', frontSrc: '/walls/bastion-wall-t1-front.webp', ringTop: 0.1, ringBottom: 0.9 },
  { minLevel: 3, rearSrc: '/walls/bastion-wall-t3-rear.webp', frontSrc: '/walls/bastion-wall-t3-front.webp', ringTop: 0.15, ringBottom: 0.95 },
  // Re-rendered to match the L1/L3 layout. The previous pair was framed
  // differently enough to break the ladder in two ways at once: it carried
  // baked ground shadows and lamp bloom the first two tiers don't have, and
  // its band fractions (0.659 / 0.630) made the ring DRAW 21-27% taller than
  // L1/L3, so the wall visibly jumped in size on upgrade. Both replacements
  // measure 0.800 / 0.794 — the same band L1 and L3 ship — which puts the
  // drawn height back at ~1.28x displayHeight across all four tiers.
  //
  // Band fractions are still measured per source, not copied between tiers:
  // these two spill slightly more above the band than L1 does (turret masts
  // and the taller gate cupola), which is exactly what ringTop encodes.
  // scaleMultiplier 0.87: these two renders frame their ring slightly tighter
  // than L1/L3 do, so at a matched band they read ~13% too large against the
  // same grid. Corrected here rather than by fudging the measured band, which
  // would have squashed them vertically instead of scaling them.
  { minLevel: 5, rearSrc: '/walls/bastion-wall-t5-rear.webp', frontSrc: '/walls/bastion-wall-t5-front.webp', ringTop: 0.131, ringBottom: 0.931, scaleMultiplier: 0.87 },
  { minLevel: 8, rearSrc: '/walls/bastion-wall-t8-rear.webp', frontSrc: '/walls/bastion-wall-t8-front.webp', ringTop: 0.137, ringBottom: 0.931, scaleMultiplier: 0.87 },
];

/** The highest tier whose minLevel is met, or null below level 1 (no wall built, no art to draw). */
export function wallArtForLevel(level: number): WallArtTier | null {
  let match: WallArtTier | null = null;
  for (const tier of WALL_ART_TIERS) {
    if (level >= tier.minLevel) match = tier;
  }
  return match;
}

/**
 * Module-level image cache, mirroring unit-art.ts's tintCache — there are
 * only 8 files total (4 tiers x 2 halves), so every one is loaded once for
 * the page's lifetime, not once per colony-view mount. Without this, tabbing
 * away from Colony and back would construct fresh Image() objects and
 * re-request every time.
 */
const wallImageCache = new Map<string, HTMLImageElement>();
// In-flight loads, keyed the same as wallImageCache — mirrors unit-art.ts's
// tintCache exactly (a Map of src -> Promise, not a Set of "is it loading").
// A Set would only tell a second caller arriving mid-load to back off; it
// has no way to hand that caller the eventual result, so it would never get
// its onLoaded call and its one synchronous draw() (already run by the time
// the load resolves) would show no ring until an unrelated redraw happened
// to come along. Storing the promise lets every caller await the SAME load.
const wallImageLoading = new Map<string, Promise<HTMLImageElement>>();

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${src}`));
    img.src = src;
  });
}

/** Already-loaded image for a src (rearSrc or frontSrc), if any — a synchronous, cache-only lookup. */
export function loadedWallImage(src: string): HTMLImageElement | undefined {
  return wallImageCache.get(src);
}

/**
 * Kicks off loading every known tier's rear AND front art that isn't already
 * cached, and calls `onLoaded` once a given tier becomes available —
 * including for a src some earlier caller already started loading (this
 * caller awaits that same in-flight promise rather than being silently
 * dropped). `onLoaded` fires per src resolved (once for rearSrc, once for
 * frontSrc — the caller just redraws either way), not only once both halves
 * are in. Safe to call from every mount: a cache hit (the common case after
 * the first ever load) is a no-op, since the caller's own synchronous draw()
 * already picked it up via loadedWallImage. No retry on failure: a src
 * simply never becomes available in the cache, and drawCityScene already
 * treats an unavailable half as "don't draw that half".
 */
export function preloadWallArt(onLoaded: (tier: WallArtTier) => void): void {
  for (const tier of WALL_ART_TIERS) {
    for (const src of [tier.rearSrc, tier.frontSrc]) {
      if (wallImageCache.has(src)) continue;

      const inFlight = wallImageLoading.get(src);
      if (inFlight) {
        inFlight.then(() => onLoaded(tier)).catch(() => {});
        continue;
      }

      const promise = loadImage(src);
      wallImageLoading.set(src, promise);
      promise
        .then((img) => {
          wallImageCache.set(src, img);
          onLoaded(tier);
        })
        .catch(() => {
          /* no fallback — see doc comment above */
        })
        .finally(() => wallImageLoading.delete(src));
    }
  }
}

/** Test seam — the cache is process-wide and would leak between cases. */
export function clearWallArtCache(): void {
  wallImageCache.clear();
  wallImageLoading.clear();
}
